import { ViewData, Answer, Question } from "@vue-skuilder/courseware";
import {
  FieldType,
  DataShape,
  DataShapeName,
  Status,
} from "@vue-skuilder/common";
import { standardFluteFingerings } from "../data/fluteFingeringChart";
import { shouldPreferFlat, midiToNoteName } from "../pitch/notationUtils";
import { NoteName } from "src/pitch";

/**
 * Answer format for sequenced note reading questions
 */
export interface SequencedNoteReadingAnswer extends Answer {
  sequence: string[];
  completed: boolean;
  wrongAttempts: number;
  timeElapsed: number; // Time in milliseconds
}

/**
 * Seed data generation
 * TODO: seedData is currently broken due to MIDI format migration.
 * See src/dbg/DebugSequencedNoteReadingQuestion.vue for working example presets.
 */
const generateSeedData = () => {
  return [];
};

export class SequencedNoteReadingQuestion extends Question {
  public static dataShapes: DataShape[] = [
    {
      name: "SequencedNoteReadingQuestion" as DataShapeName,
      fields: [
        {
          name: 'sequence',
          type: FieldType.STRING,
          validator: {
            instructions: 'Enter a comma-separated sequence of MIDI notes (e.g., "60, 62, 64, 65, 67"). Optional decorators: # (force sharp), b (force flat), n (force natural). Example: "60, 61#, 61b, 61n"',
            test: (value: unknown) => {
              if (typeof value !== 'string') {
                return {
                  status: Status.error,
                  msg: 'Sequence must be a string',
                };
              }

              // Parse comma-separated string
              const notes = value.split(',').map(s => s.trim()).filter(s => s.length > 0);

              if (notes.length < 2) {
                return {
                  status: Status.error,
                  msg: 'Sequence must contain at least 2 notes',
                };
              }

              if (notes.length > 20) {
                return {
                  status: Status.error,
                  msg: 'Sequence cannot exceed 20 notes',
                };
              }

              // Validate each note - MIDI format with optional decorator
              const midiRegex = /^(\d+)([#bn])?$/;
              for (const note of notes) {
                const match = note.match(midiRegex);
                if (!match) {
                  return {
                    status: Status.error,
                    msg: `Invalid note format: ${note}. Use MIDI number (60-96) with optional decorator (#, b, n). Example: 60, 61#, 61b`,
                  };
                }

                const midi = parseInt(match[1]);

                // Flute range: C4 (MIDI 60) to C7 (MIDI 96)
                if (midi < 60 || midi > 96) {
                  return {
                    status: Status.error,
                    msg: `MIDI ${midi} out of flute range (60-96)`,
                  };
                }
              }

              return {
                status: Status.ok,
                msg: '',
              };
            },
          },
        },
        {
          name: 'toleranceCents',
          type: FieldType.NUMBER,
          validator: {
            instructions: 'Pitch tolerance in cents (10-45, default 40)',
            test: (value: unknown) => {
              const num = Number(value);
              if (isNaN(num)) {
                return {
                  status: Status.error,
                  msg: 'Tolerance must be a number',
                };
              }

              if (num < 10 || num > 45) {
                return {
                  status: Status.error,
                  msg: 'Tolerance must be between 10 and 45 cents',
                };
              }

              return {
                status: Status.ok,
                msg: '',
              };
            },
          },
        },
        {
          name: 'showFingerings',
          type: FieldType.STRING,
          validator: {
            instructions: 'Show fingering diagrams? (true/false, default: true)',
            test: (value: unknown) => {
              // Allow undefined for backward compatibility - defaults to true
              if (value === undefined || value === null) {
                return {
                  status: Status.ok,
                  msg: '',
                };
              }
              if (!(value == 'true' || value == 'false' || value == '')) {
                return {
                  status: Status.error,
                  msg: 'Must be `true` or `false`',
                };
              }
              return {
                status: Status.ok,
                msg: '',
              };
            },
          },
        },
        {
          name: 'label',
          type: FieldType.STRING,
          validator: {
            instructions: 'Optional: Descriptive label for this melody (optional)',
            test: (value: unknown) => {
              // Allow undefined or empty string - label is optional
              if (value === undefined || value === null || value === '') {
                return {
                  status: Status.ok,
                  msg: '',
                };
              }
              if (typeof value !== 'string') {
                return {
                  status: Status.error,
                  msg: 'Label must be a string',
                };
              }
              return {
                status: Status.ok,
                msg: '',
              };
            },
          },
        },
        {
          name: 'keySig',
          type: FieldType.STRING,
          validator: {
            instructions: 'Optional: Key signature (e.g., "C major", "G major", "D minor", "F major")',
            test: (value: unknown) => {
              // Allow undefined or empty string - keySig is optional
              if (value === undefined || value === null || value === '') {
                return {
                  status: Status.ok,
                  msg: '',
                };
              }
              if (typeof value !== 'string') {
                return {
                  status: Status.error,
                  msg: 'Key signature must be a string',
                };
              }
              return {
                status: Status.ok,
                msg: '',
              };
            },
          },
        },
      ],
    },
  ];

  public static views: any[] = [];
  public static acceptsUserData: boolean = true;
  public static seedData = generateSeedData();

  // Question properties
  public sequence: NoteName[];
  public toleranceCents: number;
  public showFingerings: boolean;
  public label?: string;
  public keySig?: string;

  constructor(data: ViewData[]) {
    super(data);

    // Optional key signature (needed for spelling determination)
    this.keySig = data[0].keySig as string | undefined;

    // Parse MIDI sequence from comma-separated string
    const sequenceStr = data[0].sequence as string;
    const midiTokens = sequenceStr.split(',').map(s => s.trim()).filter(s => s.length > 0);

    // Determine default spelling preference from key signature
    const preferFlat = shouldPreferFlat(this.keySig);

    // Convert MIDI tokens to note names
    this.sequence = midiTokens.map(token => {
      // Parse MIDI number and optional decorator
      const match = token.match(/^(\d+)([#bn])?$/);
      if (!match) {
        throw new Error(`Invalid MIDI token: ${token}`);
      }

      const midi = parseInt(match[1]);
      const decorator = match[2];

      // Determine spelling based on decorator or key signature
      let noteName: NoteName;

      if (decorator === '#') {
        // Force sharp spelling (e.g., 61# → C#)
        noteName = midiToNoteName(midi, false); // preferFlat = false
      } else if (decorator === 'b') {
        // Force flat spelling (e.g., 61b → Db)
        noteName = midiToNoteName(midi, true); // preferFlat = true
      } else if (decorator === 'n') {
        // Natural decorator: force natural note spelling
        // Check if this MIDI represents a natural note (white key)
        const pitchClass = midi % 12;
        const naturalPitchClasses = [0, 2, 4, 5, 7, 9, 11]; // C, D, E, F, G, A, B

        if (!naturalPitchClasses.includes(pitchClass)) {
          // This is a black key - cannot be a natural note
          throw new Error(`Natural decorator 'n' used on non-natural pitch (MIDI ${midi}). Natural notes are C, D, E, F, G, A, B only.`);
        }

        // This is a white key - render as natural note
        noteName = midiToNoteName(midi, preferFlat);
      } else {
        // No decorator: use key signature preference
        noteName = midiToNoteName(midi, preferFlat);
      }

      return noteName;
    });

    this.toleranceCents = data[0].toleranceCents as number;

    // Default to true for backward compatibility
    this.showFingerings = data[0].showFingerings !== undefined
      ? (data[0].showFingerings as string) === 'true'
      : true;

    // Optional label
    this.label = data[0].label as string | undefined;
  }

  public dataShapes(): DataShape[] {
    return SequencedNoteReadingQuestion.dataShapes;
  }

  public views() {
    return SequencedNoteReadingQuestion.views;
  }

  evaluate(response: Answer): number {
    const answer = response as SequencedNoteReadingAnswer;

    if (!answer.completed) {
      return 0;
    }

    // Base score for completion
    let score = 0.7;

    // Bonus for accuracy (fewer wrong attempts)
    const wrongRatio = answer.wrongAttempts / answer.sequence.length;
    if (wrongRatio === 0) {
      score += 0.3; // Perfect = 100%
    } else if (wrongRatio < 0.5) {
      score += 0.2; // Good = 90%
    } else if (wrongRatio < 1.0) {
      score += 0.1; // Okay = 80%
    }
    // More than 1 wrong attempt per note = 70%

    return score;
  }
}
